import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="slide-assistant"
//
// Toggles a right-side drawer that hosts the assistant. The drawer has
// two modes:
//
//   - menu mode (default when the FAB pill is clicked, no section): shows
//     a few quick-start buttons (Events, Announcements) so the user can
//     start a fresh chat without picking a section first.
//
//   - iframe mode (when a section is clicked, or the user picks a menu
//     button): hosts the assistant chat in an iframe scoped to that
//     subject — a Slide via taskUrlPattern, an intent via intentUrlPattern.
//
// Both URL patterns carry a token (`__ID__` / `__INTENT__`) substituted
// at runtime.
export default class extends Controller {
  static targets = ["drawer", "backdrop", "iframe", "menu", "title", "fragmentEditButton"]
  static values = {
    taskUrlPattern: String,
    intentUrlPattern: String,
    rowUrlPattern: String,
    columnUrlPattern: String,
    pageUrlPattern: String,
    currentSlideId: String
  }

  connect() {
    // Document-level listener so clicks anywhere on the page (outside this
    // controller's element scope) can open the drawer. The FAB host lives
    // in a fixed corner of the layout and is `data-turbo-permanent`, so
    // it can't reach into the page's section elements via data-action.
    this.boundPageClick = this.handlePageClick.bind(this)
    this.boundKeydown = this.handleKeydown.bind(this)
    this.boundFragmentEnter = this.handleFragmentEnter.bind(this)
    this.boundFragmentLeave = this.handleFragmentLeave.bind(this)
    document.addEventListener("click", this.boundPageClick)
    document.addEventListener("keydown", this.boundKeydown)
    // Fragment hover affordance — show a floating "Edit" overlay near
    // the fragment under the cursor. Underlying links/buttons stay
    // fully clickable since the edit overlay is a separate DOM element.
    document.addEventListener("mouseover", this.boundFragmentEnter)
    document.addEventListener("mouseout", this.boundFragmentLeave)
    this.activeFragment = null
  }

  disconnect() {
    document.removeEventListener("click", this.boundPageClick)
    document.removeEventListener("keydown", this.boundKeydown)
    document.removeEventListener("mouseover", this.boundFragmentEnter)
    document.removeEventListener("mouseout", this.boundFragmentLeave)
  }

  handlePageClick(event) {
    // Fragment clicks are handled by the openFragment Stimulus action
    // bound directly on each fragment element (see frag_attrs helper).
    // This document-level handler covers slide / row / column delegation only.
    if (event.target.closest("a, button, input, textarea, select, label")) return

    const generic = event.target.closest(
      "[data-edit='row'][data-row-id], [data-edit='slide'][data-slide-id], [data-edit='column'][data-column-id], [data-edit='page'][data-page-id]"
    )
    if (generic) {
      event.preventDefault()
      const kind = generic.dataset.edit
      const id   = generic.dataset[`${kind}Id`]
      this.openFor(kind, id)
      return
    }

    // Back-compat: legacy [data-slide-edit][data-slide-id] still works.
    const legacy = event.target.closest("[data-slide-edit][data-slide-id]")
    if (legacy) {
      event.preventDefault()
      this.openFor("slide", legacy.dataset.slideId)
    }
  }

  // Show the floating edit overlay near a fragment when it's hovered.
  // Also keeps the overlay open when the cursor lands on the overlay
  // itself (so the user can travel from text to the Edit button).
  handleFragmentEnter(event) {
    if (!this.hasFragmentEditButtonTarget) return
    const overlay = this.fragmentEditButtonTarget

    // Cursor moved onto the overlay — cancel any pending hide.
    if (event.target.closest && event.target.closest(".sh-frag-edit-overlay") === overlay) {
      this.cancelHideOverlay()
      return
    }

    const frag = event.target.closest("[data-edit='fragment'][data-slide-id][data-fragment]")
    if (!frag) return
    this.cancelHideOverlay()
    this.positionFragmentEditOverlay(frag)
  }

  // Hide the overlay after a short delay when the cursor leaves the
  // fragment — the delay is enough for the user to travel across the
  // small gap between the text and the floating Edit button without
  // the button vanishing under them.
  handleFragmentLeave(event) {
    if (!this.hasFragmentEditButtonTarget) return
    const related = event.relatedTarget
    const overlay = this.fragmentEditButtonTarget
    const stillOnFragment = related && related.closest && related.closest("[data-edit='fragment'][data-slide-id][data-fragment]")
    const onOverlay = related && (related === overlay || (overlay.contains && overlay.contains(related)))
    if (stillOnFragment || onOverlay) return
    this.scheduleHideOverlay()
  }

  scheduleHideOverlay() {
    this.cancelHideOverlay()
    this.hideTimer = setTimeout(() => {
      if (this.hasFragmentEditButtonTarget) {
        this.fragmentEditButtonTarget.hidden = true
      }
      this.activeFragment = null
      this.hideTimer = null
    }, 250)
  }

  cancelHideOverlay() {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer)
      this.hideTimer = null
    }
  }

  positionFragmentEditOverlay(frag) {
    const overlay = this.fragmentEditButtonTarget
    const rect = frag.getBoundingClientRect()
    overlay.style.left = `${window.scrollX + rect.right + 8}px`
    overlay.style.top = `${window.scrollY + rect.top - 4}px`
    overlay.dataset.slideId = frag.dataset.slideId
    overlay.dataset.fragment = frag.dataset.fragment
    overlay.hidden = false
    this.activeFragment = frag
  }

  // Stimulus action — bound on the floating overlay button. Opens the
  // assistant scoped to whichever fragment was last hovered. Captures the
  // fragment's visible textContent so the drawer header can show what the
  // user actually sees on the page (the slide's fragment value may be
  // empty when the partial is rendering its default string).
  editFragment(event) {
    event?.preventDefault()
    const overlay = event.currentTarget
    const slideId = overlay.dataset.slideId
    const fragmentName = overlay.dataset.fragment
    if (!slideId || !fragmentName) return
    const visibleText = this.activeFragment?.textContent?.replace(/\s+/g, " ").trim() || ""
    this.openFor("slide", slideId, {
      fragment: fragmentName,
      fragmentText: visibleText,
      activeElement: this.activeFragment
    })
    overlay.hidden = true
  }

  handleKeydown(event) {
    if (event.key === "Escape" && !this.drawerTarget.hidden) {
      this.close()
    }
  }

  // Stimulus action — fires when the FAB pill is clicked. Opens the
  // drawer in menu mode (no section context).
  openAction(event) {
    event?.preventDefault()
    this.openMenu()
  }

  pickIntent(event) {
    event?.preventDefault()
    const intent = event?.params?.intent
    if (!intent || !this.intentUrlPatternValue) return
    const url = this.intentUrlPatternValue.replace("__INTENT__", encodeURIComponent(intent))
    this.showIframe(url)
    this.setTitle("Assistant")
  }

  openFor(kind, id, options = {}) {
    if (!id) return
    const pattern = this.urlPatternFor(kind)
    if (!pattern) return
    let url = pattern.replace("__ID__", id)
    if (options.fragment) {
      const sep = url.includes("?") ? "&" : "?"
      url = `${url}${sep}fragment=${encodeURIComponent(options.fragment)}`
      if (options.fragmentText) {
        url = `${url}&fragment_text=${encodeURIComponent(options.fragmentText)}`
      }
    }
    if (this.iframeTarget.src !== url) {
      this.iframeTarget.src = url
    }
    if (kind === "slide") this.currentSlideIdValue = id

    // markActive paints the outline. When a specific fragment element was
    // clicked, outline ONLY that element. Otherwise outline all elements
    // matching the (kind, id).
    if (options.activeElement) {
      this.markActiveElement(options.activeElement)
    } else {
      this.markActive(kind, id)
    }

    this.showIframe()
    this.openDrawer()
  }

  // Outline a single specific element (used for fragment-level clicks).
  markActiveElement(el) {
    document.querySelectorAll("[data-assistant-active]").forEach((other) => {
      other.removeAttribute("data-assistant-active")
    })
    el.setAttribute("data-assistant-active", "true")
  }

  // Add `data-assistant-active` to every element that points at the same
  // (kind, id) the drawer is currently scoped to, so CSS can paint a
  // persistent "you are here" outline. Multiple elements may match — the
  // assistant edits a slide, but several rendered blocks may carry the
  // same data-slide-id.
  markActive(kind, id) {
    document.querySelectorAll("[data-assistant-active]").forEach((el) => {
      el.removeAttribute("data-assistant-active")
    })
    if (!kind || !id) return
    const idAttr = `data-${kind}-id`
    document.querySelectorAll(`[data-edit="${kind}"][${idAttr}="${id}"]`).forEach((el) => {
      el.setAttribute("data-assistant-active", "true")
    })
    // Legacy template_02 markup uses data-slide-edit instead of data-edit="slide".
    if (kind === "slide") {
      document.querySelectorAll(`[data-slide-edit][data-slide-id="${id}"]`).forEach((el) => {
        el.setAttribute("data-assistant-active", "true")
      })
    }
  }

  urlPatternFor(kind) {
    switch (kind) {
      case "slide":  return this.taskUrlPatternValue
      case "row":    return this.rowUrlPatternValue
      case "column": return this.columnUrlPatternValue
      case "page":   return this.pageUrlPatternValue
      default:       return null
    }
  }

  openForSlide(slideId) { this.openFor("slide", slideId) }

  openMenu() {
    this.menuTarget.hidden = false
    this.iframeTarget.hidden = true
    this.setTitle("Assistant")
    this.openDrawer()
  }

  showIframe(src) {
    if (src && this.iframeTarget.src !== src) {
      this.iframeTarget.src = src
    }
    this.menuTarget.hidden = true
    this.iframeTarget.hidden = false
  }

  openDrawer() {
    this.drawerTarget.hidden = false
    this.backdropTarget.hidden = false
    document.body.classList.add("sh-fab-open")
  }

  close(event) {
    event?.preventDefault()
    this.drawerTarget.hidden = true
    this.backdropTarget.hidden = true
    document.body.classList.remove("sh-fab-open")
    this.markActive(null, null)
  }

  setTitle(text) {
    if (!this.hasTitleTarget) return
    this.titleTarget.innerHTML = `<i class="fa fa-wand-magic-sparkles mr-2"></i>${text}`
  }
}
